import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import _ from 'lodash'
import '@fil-global/gds-webcomponents/fil-icon/fil-icon.component'
import { getDeviceType, SMALL } from '@fil-global/gds-components/scripts/util/util'

import { updateUrlWithParams, parseQueryString } from 'utils/url'
import useComponentVisible from '../../hoc/useComponentVisible'
import Loader from '../../atoms/Loader'

import './style.scss'

const Tabs = ({ id, defaultActiveIndex, tabsConfig, moreContent, onTabChange, useHash }) => {
  const [activeIndex, setActiveIndex] = useState()
  const [showMoreButton, setShowMoreButtonStatus] = useState(false)
  const [hiddenTabs, setHiddenTabs] = useState([])
  const [savedHiddenTabs, setSavedHiddenTabs] = useState([])
  const [hasIcon, setHasIcon] = useState(false)

  const [tabsPanels, setTabsPanels] = useState()
  const { ref, isComponentVisible: showMoreMenu, setIsComponentVisible: setShowMoreMenuStatus } = useComponentVisible(false)

  const tabsSlot = {
    tabs: tabsConfig.tabs
  }

  const isMobile = () => {
    const breakpoint = getDeviceType()
    return breakpoint === SMALL
  }

  const getTabName = () => {
    let tabName = ''
    let urlWithoutTabName = ''
    const { fragmentIdentifier, url } = parseQueryString(window.location.href)

    const splitArray = url
      .split('funds/')[1]
      .split('/')
      .filter((path) => path !== '')

    const splitTabName = splitArray.length > 1 ? _.last(splitArray) : ''
    const avaliableTab = _.filter(tabsConfig.tabs, (tab) => tab.urlHash === splitTabName)

    if (useHash && fragmentIdentifier) {
      tabName = fragmentIdentifier
    }

    if (!_.isEmpty(avaliableTab)) {
      tabName = avaliableTab[0].urlHash
      const [firstElement] = url.split(tabName)
      urlWithoutTabName = firstElement
    } else {
      const [firstElement] = splitTabName ? url.split(splitTabName) : [url]
      urlWithoutTabName = firstElement
    }

    return { tabName, urlWithoutTabName: _.endsWith(urlWithoutTabName, '/') ? urlWithoutTabName : `${urlWithoutTabName}/` }
  }

  const handleTabChange = async ({ tabIndex, tabUrlHash }) => {
    if (tabsPanels) {
      setActiveIndex(tabIndex)
      setTabsPanels()
      if (tabUrlHash) {
        if (useHash) {
          updateUrlWithParams({ fragmentIdentifier: tabUrlHash })
        } else {
          const { urlWithoutTabName } = getTabName()

          updateUrlWithParams({ url: `${urlWithoutTabName}${tabUrlHash}` })
        }
      }

      onTabChange({ tabIndex, tabUrlHash })
      const newPanelInfo = await tabsConfig.panelsFunctions[tabIndex]()

      const newPanels = tabsPanels.map((panel, panelIndex) => {
        return {
          element: panelIndex === tabIndex ? newPanelInfo : null
        }
      })

      setShowMoreMenuStatus(false)
      setTabsPanels(newPanels)
    }
  }

  const handleShowMoreMenu = () => {
    setShowMoreMenuStatus(!showMoreMenu)
  }

  const handleShowMoreMenuChange = ({ tabIndex, tabUrlHash }) => {
    const newHiddenTabs = [_.first(savedHiddenTabs) - 1, ...savedHiddenTabs].filter((value) => tabIndex !== value)

    setHiddenTabs(newHiddenTabs)
    setShowMoreMenuStatus(false)
    handleTabChange({ tabIndex, tabUrlHash })
  }

  const generateTabItems = (tabsCollection, clickFunc, isMenuTab) => {
    const tabsElements = tabsCollection.map((tabItem, tabIndex) => (
      <li
        {...(id ? { id: `${id}_tab_${tabItem.urlHash}` } : {})}
        key={`tabs-${id}-tab-${tabIndex}`}
        className={classnames('tabs-title', {
          'is-disabled': tabItem.disabled,
          'is-active': activeIndex === tabIndex,
          'fil--hidden': isMenuTab ? !hiddenTabs.includes(tabIndex) : hiddenTabs.includes(tabIndex)
        })}
        data-index={tabIndex}
        tabIndex={tabIndex}
        role="tab"
        aria-label={tabItem.label}
        aria-selected={activeIndex === tabIndex}
        onClick={() => {
          clickFunc({ tabIndex, tabUrlHash: tabItem.urlHash })
        }}
      >
        {tabItem.icon && <fil-icon class="fi-icon" icon={tabItem.icon} size="24" />}
        <a
          tabIndex="-1"
          className={classnames({
            'is-disabled-link': !!tabItem.disabled
          })}
        >
          {tabItem.label}
        </a>
      </li>
    ))

    return tabsElements
  }

  const calculateLinks = () => {
    const primary = document.querySelectorAll('.fil-tabs--wrapper .tabs')
    const primaryItems = document.querySelectorAll('.fil-tabs--wrapper .tabs > li:not(.fil-more--menu)')
    let stopWidth = 90
    const hiddenItems = []

    const primaryWidth = primary[0].offsetWidth
    primaryItems.forEach((item, i) => {
      if (primaryWidth >= stopWidth + item.offsetWidth) {
        stopWidth += item.offsetWidth
      } else {
        hiddenItems.push(i)
      }
    })

    setHiddenTabs(hiddenItems)
    setSavedHiddenTabs(hiddenItems)
    setShowMoreButtonStatus(!!hiddenItems.length)
  }

  const clearHiddenTabs = () => {
    setHiddenTabs([])
  }

  const handleResize = () => {
    clearHiddenTabs()
    if (!isMobile()) {
      calculateLinks()
    }
  }

  useEffect(async () => {
    window.addEventListener('resize', handleResize)

    const { tabName, urlWithoutTabName } = getTabName()
    const { panels, panelsFunctions, tabs } = tabsConfig
    let selectedIndex = defaultActiveIndex

    if (tabName) {
      tabs.forEach((tabItem, tabItemIndex) => {
        if (tabItem.urlHash === tabName) {
          selectedIndex = tabItemIndex
        }
      })
    }

    if (!tabName) {
      updateUrlWithParams({
        ...(useHash ? { fragmentIdentifier: tabs[selectedIndex].urlHash } : { url: `${urlWithoutTabName}${tabs[selectedIndex].urlHash}` })
      })
    }

    setActiveIndex(selectedIndex)
    onTabChange({ tabIndex: selectedIndex, tabUrlHash: tabName })

    const activePanel = await panelsFunctions[selectedIndex]()
    panels[selectedIndex].element = activePanel

    const iconTabs = tabsSlot.tabs.filter((tab) => {
      return !!tab.icon
    })

    setHasIcon(!!iconTabs.length)

    calculateLinks()

    setTabsPanels(panels)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  return (
    <div
      {...(id ? { id } : {})}
      className={classnames('fil-tabs--wrapper is--jsfied', {
        'show-secondary': showMoreMenu
      })}
    >
      <ul
        {...(id ? { id: `${id}_tabs` } : {})}
        className={classnames('tabs', {
          'fil-icons--type': hasIcon
        })}
        role="tablist"
        aria-label="tablist"
      >
        {generateTabItems(tabsSlot.tabs, handleTabChange)}
        {showMoreButton && (
          <li className="fil-more--menu">
            <button type="button" aria-haspopup="true" aria-expanded="true" onClick={handleShowMoreMenu}>
              <fil-icon icon="navigation-menu-horizontal" filled class="fil-icon--more" size="16" />
              <span className="fil-more">{moreContent}</span>
            </button>
            <ul ref={ref} className="tab-secondary">
              {generateTabItems(tabsSlot.tabs, handleShowMoreMenuChange, true)}
            </ul>
          </li>
        )}
      </ul>
      <div className="tabs-content">
        {tabsPanels ? (
          tabsPanels.map((panel, panelIndex) => {
            return (
              <div
                key={`tabs-${id}-panel-${panelIndex}`}
                className={classnames('tabs-panel', {
                  [activeIndex === panelIndex ? 'show' : 'hidden']: true
                })}
              >
                {panel.element}
              </div>
            )
          })
        ) : (
          <Loader />
        )}
      </div>
    </div>
  )
}

Tabs.propTypes = {
  tabsConfig: PropTypes.shape({
    tabs: PropTypes.array.isRequired,
    panels: PropTypes.array.isRequired,
    panelsFunctions: PropTypes.array.isRequired
  }).isRequired,
  id: PropTypes.string,
  defaultActiveIndex: PropTypes.number,
  moreContent: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  onTabChange: PropTypes.func,
  useHash: PropTypes.bool
}

Tabs.defaultProps = {
  id: '',
  defaultActiveIndex: 0,
  moreContent: 'More',
  onTabChange: () => {},
  useHash: false
}

export default Tabs
